import { useMemo, useRef, useState } from "react";
import {
  Archive,
  Check,
  ChevronDown,
  ChevronUp,
  Clock3,
  History,
  Pencil,
  Plus,
  Save,
  Search,
  Send,
  X,
} from "lucide-react";

import { Badge, Button, Card } from "@/design-system/primitives";
import {
  useArchiveClinicianMessageAutomation,
  useClinicianMessageAutomationDeliveries,
  useClinicianMessageAutomations,
  useClinicianProducts,
  useClinicianSession,
  useCreateClinicianMessageAutomation,
  useUpdateClinicianMessageAutomation,
} from "@/lib/api/clinicianHooks";
import type {
  ClinicianMessageAutomation,
  ClinicianMessageAutomationDeliveryStatus,
  ClinicianMessageAutomationInput,
  ClinicianMessageAutomationMedicationScope,
  ClinicianMessageAutomationTrigger,
  ClinicianProduct,
} from "@/types/clinician";

const fieldClass =
  "min-h-38 border border-line bg-raised px-9 type-body-sm text-ink outline-none focus:shadow-focus disabled:opacity-40";

const TRIGGERS: {
  value: ClinicianMessageAutomationTrigger;
  label: string;
}[] = [
  { value: "rx_written", label: "Rx written" },
  { value: "rx_shipped", label: "Rx shipped" },
  { value: "rx_delivered", label: "Rx delivered" },
];

const MERGE_FIELDS = [
  "{{patient_first_name}}",
  "{{patient_last_name}}",
  "{{patient_full_name}}",
  "{{medication_name}}",
  "{{clinician_name}}",
] as const;

type DelayUnit = "minutes" | "hours" | "days";

interface AutomationDraft extends ClinicianMessageAutomationInput {
  delay_amount: number;
  delay_unit: DelayUnit;
}

const NEW_AUTOMATION: AutomationDraft = {
  name: "",
  enabled: false,
  trigger_event: "rx_written",
  delay_minutes: 60,
  delay_amount: 1,
  delay_unit: "hours",
  medication_scope: "all",
  product_ids: [],
  message_template: "",
};

export function MessageAutomations() {
  const automations = useClinicianMessageAutomations();
  const products = useClinicianProducts();
  const session = useClinicianSession();
  const [adding, setAdding] = useState(false);
  const currentSender =
    session.data?.display_name ||
    session.data?.email ||
    "your mapped clinician account";

  return (
    <section className="flex flex-col gap-12">
      <div className="flex flex-wrap items-end justify-between gap-12">
        <div>
          <h2 className="type-heading-md">Event-based patient messages</h2>
          <p className="mt-4 max-w-[70rem] type-body-sm text-ink-muted">
            Send a clinician-authored message after a prescription milestone.
            New automations start off and do not apply to past events.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setAdding(true)}>
          <Plus size={13} aria-hidden /> New automation
        </Button>
      </div>

      <div className="border border-line bg-sunken px-12 py-9 type-body-sm text-ink-secondary">
        Messages created here send as <strong>{currentSender}</strong>. If that
        clinician's Healthie identity is unavailable, the delivery is held for
        review rather than sent from another account.
      </div>

      {adding && (
        <AutomationEditor
          products={products.data ?? []}
          senderName={currentSender}
          onClose={() => setAdding(false)}
        />
      )}

      {automations.isLoading ? (
        <div className="h-[18rem] animate-pulse border border-line-subtle bg-sunken" />
      ) : automations.isError ? (
        <p
          role="alert"
          className="border border-danger/30 bg-danger-wash p-14 type-body-sm text-danger"
        >
          Message automations could not be loaded. Nothing was changed.
        </p>
      ) : automations.data?.items.length ? (
        <div className="overflow-hidden border border-line-subtle bg-raised">
          <div className="hidden grid-cols-[minmax(15rem,1fr)_minmax(16rem,1fr)_minmax(14rem,0.8fr)_minmax(14rem,0.8fr)_9rem] gap-9 border-b border-line-subtle bg-sunken px-11 py-7 type-label-xs text-ink-muted lg:grid">
            <span>AUTOMATION</span>
            <span>WHEN</span>
            <span>MEDICATIONS</span>
            <span>DELIVERIES</span>
            <span />
          </div>
          <div className="divide-y divide-line-subtle">
            {automations.data.items.map((automation) => (
              <AutomationRow
                key={automation.id}
                automation={automation}
                products={products.data ?? []}
              />
            ))}
          </div>
        </div>
      ) : (
        <Card className="flex flex-col items-center gap-8 py-28 text-center">
          <Send size={22} className="text-ink-muted" aria-hidden />
          <p className="type-heading-sm">No message automations yet</p>
          <p className="max-w-[48rem] type-body-sm text-ink-muted">
            Create one when you are ready. It will stay off until you review and
            explicitly enable it.
          </p>
        </Card>
      )}
    </section>
  );
}

function AutomationRow({
  automation,
  products,
}: {
  automation: ClinicianMessageAutomation;
  products: ClinicianProduct[];
}) {
  const update = useUpdateClinicianMessageAutomation();
  const archive = useArchiveClinicianMessageAutomation();
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const productById = useMemo(
    () => new Map(products.map((product) => [product.product_id, product])),
    [products],
  );

  const toggleEnabled = async () => {
    const next = !automation.enabled;
    if (next && !confirmEnable(automation.name)) return;
    setError(null);
    try {
      await update.mutateAsync({
        id: automation.id,
        ...editableAutomation(automation),
        enabled: next,
      });
    } catch {
      setError("The automation status could not be changed.");
    }
  };

  const archiveAutomation = async () => {
    if (
      !window.confirm(
        `Archive “${automation.name}”? Pending unsent messages will not be sent.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await archive.mutateAsync(automation.id);
    } catch {
      setError("The automation could not be archived.");
    }
  };

  const medicationLabel =
    automation.medication_scope === "all"
      ? "All medications"
      : automation.product_ids
          .map((id) => productById.get(id)?.label ?? id)
          .join(", ");
  const activityCount =
    automation.counts.sent +
    automation.counts.scheduled +
    automation.counts.skipped +
    automation.counts.cancelled +
    automation.counts.failed +
    automation.counts.quarantined;

  return (
    <article>
      <div className="grid gap-7 px-11 py-10 lg:grid-cols-[minmax(15rem,1fr)_minmax(16rem,1fr)_minmax(14rem,0.8fr)_minmax(14rem,0.8fr)_9rem] lg:items-center lg:gap-9">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-6">
            <h3 className="truncate type-heading-sm">{automation.name}</h3>
            <Badge tone={automation.enabled ? "success" : "neutral"} size="sm">
              {automation.enabled ? "ON" : "OFF"}
            </Badge>
          </div>
          <p className="mt-4 type-label-xs text-ink-muted">
            Sends as {automation.sender.display_name} · v{automation.version}
          </p>
          <p className="mt-4 line-clamp-1 type-body-sm text-ink-secondary">
            {automation.message_template}
          </p>
        </div>
        <div className="type-body-sm">
          <span className="font-medium">
            {triggerLabel(automation.trigger_event)}
          </span>
          <span className="block text-ink-muted">
            {formatDelay(automation.delay_minutes)} later
          </span>
        </div>
        <p className="line-clamp-2 type-body-sm text-ink-secondary">
          {medicationLabel}
        </p>
        <div className="type-body-sm">
          <span>{automation.counts.sent} sent</span>
          <span className="block text-ink-muted">
            {automation.counts.scheduled} scheduled
            {automation.counts.failed + automation.counts.quarantined > 0
              ? ` · ${automation.counts.failed + automation.counts.quarantined} need review`
              : ""}
          </span>
          {(automation.counts.skipped > 0 ||
            automation.counts.cancelled > 0) && (
            <span className="block text-ink-muted">
              {automation.counts.skipped} skipped ·{" "}
              {automation.counts.cancelled} cancelled
            </span>
          )}
          {automation.last_sent_at && (
            <span className="block type-label-xs text-ink-muted">
              Last sent {formatTimestamp(automation.last_sent_at)}
            </span>
          )}
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={automation.enabled}
            aria-label={`${automation.enabled ? "Disable" : "Enable"} ${automation.name}`}
            disabled={update.isPending}
            onClick={() => void toggleEnabled()}
            className={`relative block h-[22px] w-[40px] shrink-0 rounded-full border transition-colors disabled:opacity-40 ${
              automation.enabled
                ? "border-accent bg-accent"
                : "border-line-strong bg-sunken"
            }`}
          >
            <span
              className={`absolute left-0 top-[1px] h-[18px] w-[18px] rounded-full border border-line bg-white shadow transition-transform ${
                automation.enabled ? "translate-x-[19px]" : "translate-x-[1px]"
              }`}
            />
          </button>
          <button
            type="button"
            aria-label={`Edit ${automation.name}`}
            onClick={() => setEditing((current) => !current)}
            className="p-5 text-ink-muted hover:text-ink"
          >
            <Pencil size={13} aria-hidden />
          </button>
          <button
            type="button"
            aria-label={`${historyOpen ? "Hide" : "View"} delivery history for ${automation.name}`}
            onClick={() => setHistoryOpen((current) => !current)}
            className="p-5 text-ink-muted hover:text-ink"
          >
            {historyOpen ? (
              <ChevronUp size={14} aria-hidden />
            ) : (
              <History size={13} aria-hidden />
            )}
          </button>
        </div>
        {activityCount === 0 && (
          <p className="type-label-xs text-ink-muted lg:col-span-5">
            No events have matched this automation yet.
          </p>
        )}
        {error && (
          <p role="alert" className="text-11 text-danger lg:col-span-5">
            {error}
          </p>
        )}
      </div>

      {editing && (
        <AutomationEditor
          automation={automation}
          products={products}
          senderName={automation.sender.display_name}
          onClose={() => setEditing(false)}
          onArchive={() => void archiveAutomation()}
        />
      )}
      {historyOpen && <DeliveryHistory automationId={automation.id} />}
    </article>
  );
}

function AutomationEditor({
  automation,
  products,
  senderName,
  onClose,
  onArchive,
}: {
  automation?: ClinicianMessageAutomation;
  products: ClinicianProduct[];
  senderName: string;
  onClose: () => void;
  onArchive?: () => void;
}) {
  const create = useCreateClinicianMessageAutomation();
  const update = useUpdateClinicianMessageAutomation();
  const initial = useMemo(
    () => (automation ? draftFromAutomation(automation) : NEW_AUTOMATION),
    [automation],
  );
  const [draft, setDraft] = useState<AutomationDraft>(initial);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pending = create.isPending || update.isPending;
  const unknownFields = unknownMergeFields(draft.message_template);
  const delayMinutes = delayInMinutes(draft.delay_amount, draft.delay_unit);
  const valid =
    draft.name.trim().length > 0 &&
    draft.message_template.trim().length > 0 &&
    draft.delay_amount >= 0 &&
    Number.isFinite(draft.delay_amount) &&
    delayMinutes <= 129_600 &&
    (draft.medication_scope === "all" || draft.product_ids.length > 0) &&
    unknownFields.length === 0;

  const setEnabled = (next: boolean) => {
    if (next && !confirmEnable(draft.name || "this automation")) return;
    setDraft((current) => ({ ...current, enabled: next }));
  };

  const insertField = (field: (typeof MERGE_FIELDS)[number]) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? draft.message_template.length;
    const end = textarea?.selectionEnd ?? start;
    const next =
      draft.message_template.slice(0, start) +
      field +
      draft.message_template.slice(end);
    setDraft((current) => ({ ...current, message_template: next }));
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + field.length, start + field.length);
    });
  };

  const save = async () => {
    if (!valid) return;
    setError(null);
    const input = automationInput(draft);
    try {
      if (automation) {
        await update.mutateAsync({ id: automation.id, ...input });
      } else {
        await create.mutateAsync(input);
      }
      onClose();
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "The automation could not be saved. Nothing was changed.",
      );
    }
  };

  return (
    <div className="border-t border-line-subtle bg-sunken/50 px-11 py-12">
      <div className="flex items-start justify-between gap-10">
        <div>
          <p className="type-label-sm">
            {automation ? "Edit automation" : "New automation"}
          </p>
          <p className="mt-3 type-body-sm text-ink-muted">
            {automation
              ? "Changes apply to future matching events."
              : "It will be saved off unless you explicitly enable it."}
          </p>
          <p className="mt-4 type-label-xs text-ink-muted">
            SENDS AS {senderName}
          </p>
        </div>
        <button
          type="button"
          aria-label="Close automation editor"
          disabled={pending}
          onClick={onClose}
          className="p-4 text-ink-muted hover:text-ink disabled:opacity-40"
        >
          <X size={15} aria-hidden />
        </button>
      </div>

      <div className="mt-11 grid min-w-0 gap-10 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-9">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            NAME
            <input
              value={draft.name}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              maxLength={100}
              placeholder="e.g. Check in after delivery"
              className={fieldClass}
            />
          </label>

          <div className="grid grid-cols-[minmax(12rem,1fr)_8rem_11rem] gap-7">
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              TRIGGER
              <select
                value={draft.trigger_event}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    trigger_event: event.target
                      .value as ClinicianMessageAutomationTrigger,
                  }))
                }
                className={fieldClass}
              >
                {TRIGGERS.map((trigger) => (
                  <option key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              WAIT
              <input
                type="number"
                min={0}
                max={
                  draft.delay_unit === "days"
                    ? 90
                    : draft.delay_unit === "hours"
                      ? 2160
                      : 129600
                }
                value={draft.delay_amount}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    delay_amount: Number.isFinite(event.target.valueAsNumber)
                      ? event.target.valueAsNumber
                      : 0,
                  }))
                }
                aria-label="Automation delay"
                className={`${fieldClass} w-full min-w-[72px] tabular-nums`}
              />
            </label>
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              PERIOD
              <select
                value={draft.delay_unit}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    delay_unit: event.target.value as DelayUnit,
                  }))
                }
                aria-label="Automation delay unit"
                className={fieldClass}
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
            </label>
          </div>
          {delayMinutes > 129_600 && (
            <p className="text-11 text-danger">
              Choose a delay no longer than 90 days.
            </p>
          )}

          <MedicationScopePicker
            scope={draft.medication_scope}
            productIds={draft.product_ids}
            products={products}
            onChange={(medication_scope, product_ids) =>
              setDraft((current) => ({
                ...current,
                medication_scope,
                product_ids,
              }))
            }
          />
        </div>

        <div className="flex min-w-0 flex-col gap-8">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            MESSAGE
            <textarea
              ref={textareaRef}
              value={draft.message_template}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  message_template: event.target.value,
                }))
              }
              maxLength={4000}
              rows={7}
              placeholder="Write the message the patient will receive."
              className={`${fieldClass} resize-y py-8`}
            />
          </label>
          <div>
            <p className="type-label-xs text-ink-muted">INSERT FIELD</p>
            <div className="mt-5 flex flex-wrap gap-5">
              {MERGE_FIELDS.map((field) => (
                <button
                  key={field}
                  type="button"
                  onClick={() => insertField(field)}
                  className="border border-line bg-raised px-7 py-5 font-mono text-10 hover:border-ink"
                >
                  {field}
                </button>
              ))}
            </div>
          </div>
          {unknownFields.length > 0 && (
            <p className="text-11 text-danger">
              Unsupported field{unknownFields.length === 1 ? "" : "s"}:{" "}
              {unknownFields.join(", ")}
            </p>
          )}
          <div className="border border-line-subtle bg-raised p-10">
            <p className="type-label-xs text-ink-muted">PATIENT PREVIEW</p>
            <p className="mt-6 whitespace-pre-wrap type-body-sm text-ink-secondary">
              {renderPreview(draft.message_template) ||
                "Your message preview will appear here."}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-11 flex flex-wrap items-center justify-between gap-10 border-t border-line-subtle pt-10">
        <div className="flex items-center gap-8">
          <span
            className={`type-label-sm ${draft.enabled ? "text-accent" : "text-ink-muted"}`}
          >
            {draft.enabled ? "On" : "Off"}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={draft.enabled}
            aria-label="Enable patient message automation"
            disabled={pending}
            onClick={() => setEnabled(!draft.enabled)}
            className={`relative block h-[24px] w-[44px] shrink-0 rounded-full border transition-colors disabled:opacity-40 ${
              draft.enabled
                ? "border-accent bg-accent"
                : "border-line-strong bg-sunken"
            }`}
          >
            <span
              className={`absolute left-0 top-[1px] h-[20px] w-[20px] rounded-full border border-line bg-white shadow transition-transform ${
                draft.enabled ? "translate-x-[21px]" : "translate-x-[2px]"
              }`}
            />
          </button>
          <span className="type-body-sm text-ink-muted">
            {draft.enabled
              ? "Future matching events will schedule messages."
              : "No messages will be scheduled."}
          </span>
        </div>
        <div className="flex items-center gap-5">
          {automation && onArchive && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pending}
              onClick={onArchive}
            >
              <Archive size={12} aria-hidden /> Archive
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={pending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!valid}
            loading={pending}
            onClick={() => void save()}
          >
            <Save size={12} aria-hidden /> Save automation
          </Button>
        </div>
        {error && (
          <p role="alert" className="basis-full text-11 text-danger">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function MedicationScopePicker({
  scope,
  productIds,
  products,
  onChange,
}: {
  scope: ClinicianMessageAutomationMedicationScope;
  productIds: string[];
  products: ClinicianProduct[];
  onChange: (
    scope: ClinicianMessageAutomationMedicationScope,
    productIds: string[],
  ) => void;
}) {
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const query = search.trim().toLocaleLowerCase();
  const selected = new Set(productIds);
  const visible = products.filter(
    (product) =>
      !query ||
      product.label.toLocaleLowerCase().includes(query) ||
      product.product_id.toLocaleLowerCase().includes(query),
  );

  return (
    <fieldset className="flex min-w-0 flex-col gap-6">
      <legend className="type-label-xs text-ink-muted">
        MEDICATION FILTER
      </legend>
      <div className="flex gap-5">
        {(["all", "selected"] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={scope === value}
            onClick={() => {
              onChange(value, value === "all" ? [] : productIds);
              setPickerOpen(value === "selected");
            }}
            className={`border px-9 py-6 type-label-xs ${
              scope === value
                ? "border-ink bg-ink text-white"
                : "border-line bg-raised text-ink"
            }`}
          >
            {value === "all" ? "All medications" : "Specific medications"}
          </button>
        ))}
      </div>

      {scope === "selected" && (
        <div className="min-w-0 overflow-hidden border border-line-subtle bg-raised">
          <button
            type="button"
            aria-expanded={pickerOpen}
            aria-controls="automation-medication-options"
            onClick={() => setPickerOpen((current) => !current)}
            className="flex w-full min-w-0 items-center justify-between gap-8 px-9 py-7 text-left hover:bg-sunken"
          >
            <span className="min-w-0">
              <span className="block type-body-sm font-medium">
                {productIds.length
                  ? `${productIds.length} medication${productIds.length === 1 ? "" : "s"} selected`
                  : "Choose medications"}
              </span>
              <span className="block truncate type-label-xs text-ink-muted">
                {productIds.length
                  ? products
                      .filter((product) => selected.has(product.product_id))
                      .map((product) => product.label)
                      .join(", ")
                  : "Select at least one medication or treatment."}
              </span>
            </span>
            <ChevronDown
              size={14}
              aria-hidden
              className={`shrink-0 transition-transform ${pickerOpen ? "rotate-180" : ""}`}
            />
          </button>

          {pickerOpen && (
            <div
              id="automation-medication-options"
              className="min-w-0 border-t border-line-subtle p-7"
            >
              <label className="relative block min-w-0">
                <Search
                  size={13}
                  className="absolute left-9 top-1/2 -translate-y-1/2 text-ink-muted"
                  aria-hidden
                />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search medications or treatments"
                  aria-label="Search medication filters"
                  className="min-h-36 w-full min-w-0 border border-line bg-raised pl-31 pr-9 type-body-sm outline-none focus:shadow-focus"
                />
              </label>
              <div
                className="mt-6 max-h-[14rem] min-w-0 divide-y divide-line-subtle overflow-y-auto border border-line-subtle bg-raised"
                style={{ overflowAnchor: "none" }}
              >
                {visible.map((product) => {
                  const checked = selected.has(product.product_id);
                  return (
                    <button
                      key={product.product_id}
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      aria-label={product.label}
                      onClick={() =>
                        onChange(
                          scope,
                          checked
                            ? productIds.filter(
                                (id) => id !== product.product_id,
                              )
                            : [...productIds, product.product_id],
                        )
                      }
                      className="flex w-full min-w-0 items-center gap-8 px-9 py-7 text-left hover:bg-sunken focus-visible:shadow-focus"
                    >
                      <span
                        aria-hidden
                        className={`flex h-18 w-18 shrink-0 items-center justify-center border ${
                          checked
                            ? "border-ink bg-ink text-page"
                            : "border-line bg-raised"
                        }`}
                      >
                        {checked ? <Check size={11} /> : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate type-body-sm font-medium">
                          {product.label}
                        </span>
                        <span className="block truncate type-label-xs text-ink-muted">
                          {product.product_id}
                        </span>
                      </span>
                    </button>
                  );
                })}
                {!products.length && (
                  <p className="px-9 py-10 type-body-sm text-ink-muted">
                    Medication catalog is loading…
                  </p>
                )}
                {products.length > 0 && !visible.length && (
                  <p className="px-9 py-10 type-body-sm text-ink-muted">
                    No medications match “{search.trim()}”.
                  </p>
                )}
              </div>
              <div className="mt-7 flex items-center justify-between gap-8">
                <p className="type-label-xs text-ink-muted">
                  {productIds.length} selected
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setPickerOpen(false)}
                >
                  Done selecting
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}

function DeliveryHistory({ automationId }: { automationId: string }) {
  const deliveries = useClinicianMessageAutomationDeliveries(
    automationId,
    true,
  );

  return (
    <div className="border-t border-line-subtle bg-sunken/40 px-11 py-10">
      <div className="flex items-center gap-6">
        <Clock3 size={13} aria-hidden />
        <p className="type-label-sm">Recent delivery history</p>
      </div>
      {deliveries.isLoading ? (
        <div className="mt-8 h-60 animate-pulse bg-sunken" />
      ) : deliveries.isError ? (
        <p role="alert" className="mt-8 text-11 text-danger">
          Delivery history could not be loaded.
        </p>
      ) : deliveries.data?.items.length ? (
        <div className="mt-8 overflow-x-auto border border-line-subtle bg-raised">
          <table className="w-full min-w-[62rem] text-left">
            <thead className="border-b border-line-subtle bg-sunken type-label-xs text-ink-muted">
              <tr>
                <th className="px-8 py-6 font-normal">PATIENT</th>
                <th className="px-8 py-6 font-normal">MEDICATION</th>
                <th className="px-8 py-6 font-normal">SCHEDULED</th>
                <th className="px-8 py-6 font-normal">STATUS</th>
                <th className="px-8 py-6 font-normal">DETAIL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle type-body-sm">
              {deliveries.data.items.map((delivery) => (
                <tr key={delivery.id}>
                  <td className="px-8 py-7">{delivery.patient_name}</td>
                  <td className="px-8 py-7">{delivery.medication_name}</td>
                  <td className="px-8 py-7 text-ink-muted">
                    {formatTimestamp(delivery.scheduled_for)}
                  </td>
                  <td className="px-8 py-7">
                    <Badge tone={deliveryTone(delivery.status)} size="sm">
                      {delivery.status}
                    </Badge>
                  </td>
                  <td className="px-8 py-7 text-ink-muted">
                    {delivery.sent_at
                      ? `Sent ${formatTimestamp(delivery.sent_at)}`
                      : delivery.error_code || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-7 type-body-sm text-ink-muted">
          No delivery attempts yet.
        </p>
      )}
    </div>
  );
}

function editableAutomation(
  automation: ClinicianMessageAutomation,
): ClinicianMessageAutomationInput {
  return {
    name: automation.name,
    enabled: automation.enabled,
    trigger_event: automation.trigger_event,
    delay_minutes: automation.delay_minutes,
    medication_scope: automation.medication_scope,
    product_ids: automation.product_ids,
    message_template: automation.message_template,
  };
}

function draftFromAutomation(
  automation: ClinicianMessageAutomation,
): AutomationDraft {
  const delay = delayFromMinutes(automation.delay_minutes);
  return {
    ...editableAutomation(automation),
    delay_amount: delay.amount,
    delay_unit: delay.unit,
  };
}

function automationInput(
  draft: AutomationDraft,
): ClinicianMessageAutomationInput {
  return {
    name: draft.name.trim(),
    enabled: draft.enabled,
    trigger_event: draft.trigger_event,
    delay_minutes: delayInMinutes(draft.delay_amount, draft.delay_unit),
    medication_scope: draft.medication_scope,
    product_ids: draft.medication_scope === "selected" ? draft.product_ids : [],
    message_template: draft.message_template.trim(),
  };
}

function delayInMinutes(amount: number, unit: DelayUnit) {
  const multiplier = unit === "days" ? 1440 : unit === "hours" ? 60 : 1;
  return Math.round(amount * multiplier);
}

function delayFromMinutes(minutes: number): {
  amount: number;
  unit: DelayUnit;
} {
  if (minutes > 0 && minutes % 1440 === 0) {
    return { amount: minutes / 1440, unit: "days" };
  }
  if (minutes > 0 && minutes % 60 === 0) {
    return { amount: minutes / 60, unit: "hours" };
  }
  return { amount: minutes, unit: "minutes" };
}

function formatDelay(minutes: number) {
  const delay = delayFromMinutes(minutes);
  const label = delay.amount === 1 ? delay.unit.replace(/s$/, "") : delay.unit;
  return `${delay.amount} ${label}`;
}

function triggerLabel(trigger: ClinicianMessageAutomationTrigger) {
  return TRIGGERS.find((item) => item.value === trigger)?.label ?? trigger;
}

function unknownMergeFields(template: string) {
  const matches = template.match(/{{\s*[^{}]+\s*}}/g) ?? [];
  const allowed = new Set<string>(MERGE_FIELDS);
  return [...new Set(matches.filter((match) => !allowed.has(match)))];
}

function renderPreview(template: string) {
  return template
    .replaceAll("{{patient_first_name}}", "Taylor")
    .replaceAll("{{patient_last_name}}", "Morgan")
    .replaceAll("{{patient_full_name}}", "Taylor Morgan")
    .replaceAll("{{medication_name}}", "NAD+")
    .replaceAll("{{clinician_name}}", "Stephen Yun, NP");
}

function confirmEnable(name: string) {
  return window.confirm(
    `Enable “${name}”? Future matching prescription events will schedule real patient messages from your clinician account.`,
  );
}

function formatTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function deliveryTone(status: ClinicianMessageAutomationDeliveryStatus) {
  if (status === "sent") return "success" as const;
  if (status === "failed" || status === "quarantined") return "danger" as const;
  if (status === "scheduled" || status === "processing")
    return "accent" as const;
  return "neutral" as const;
}
